import { EarlyAccessDialog } from "@/components/EarlyAccessDialog";
import { Layout } from "@/components/Layout";
import { TradeConfidence } from "@/components/TradeConfidence";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Recycle, Handshake, Package, Users, CheckCircle, Search, FileText } from "lucide-react";
import { useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import heroImage from "@/assets/hero-textile-pile.jpg";
import { useLanguage } from "@/contexts/LanguageContext";

export default function Home() {
  const { t } = useLanguage();

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://hidnstock.com/#organization",
        "name": "HIDNstock",
        "url": "https://hidnstock.com/",
        "description": "B2B marketplace connecting fashion brands, sorting facilities, and redesigners through sorted secondhand, deadstock materials, and remake projects.",
        "sameAs": []
      },
      {
        "@type": "WebSite",
        "@id": "https://hidnstock.com/#website",
        "url": "https://hidnstock.com/",
        "name": "HIDNstock",
        "description": "B2B Marketplace for Sorted Secondhand & Deadstock",
        "publisher": { "@id": "https://hidnstock.com/#organization" }
      },
      {
        "@type": "WebPage",
        "@id": "https://hidnstock.com/",
        "url": "https://hidnstock.com/",
        "name": "HIDNstock — B2B Marketplace for Deadstock & Secondhand",
        "description": "A B2B marketplace connecting brands, sorting facilities and redesigners through sorted secondhand, deadstock materials and remake projects for circular fashion.",
        "isPartOf": { "@id": "https://hidnstock.com/#website" },
        "about": { "@id": "https://hidnstock.com/#organization" }
      },
      {
        "@type": "Service",
        "serviceType": "B2B Textile Marketplace",
        "provider": { "@id": "https://hidnstock.com/#organization" },
        "areaServed": "Europe",
        "description": "Buy and sell sorted secondhand and deadstock textiles, or post remake projects to transform excess inventory into sustainable fashion."
      }
    ]
  };
  const [hasInteracted, setHasInteracted] = useState(false);
  const tabContentRef = useRef<HTMLDivElement>(null);

  const handleTabChange = () => {
    setHasInteracted(true);
    setTimeout(() => {
      tabContentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <Layout>
      <Helmet>
        <title>HIDNstock — B2B Marketplace for Deadstock & Secondhand</title>
        <meta name="description" content="The B2B marketplace to source deadstock, sorted secondhand textiles and remake projects in bulk for circular fashion." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://hidnstock.com/" />
        <meta property="og:title" content="HIDNstock — B2B Marketplace for Deadstock & Secondhand" />
        <meta property="og:description" content="Source deadstock, sorted secondhand textiles and remake projects in bulk. The B2B marketplace for circular fashion." />
        <meta property="og:site_name" content="HIDNstock" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://hidnstock.com/" />
        <meta name="twitter:title" content="HIDNstock — B2B Marketplace for Deadstock & Secondhand" />
        <meta name="twitter:description" content="Source deadstock, sorted secondhand textiles and remake projects in bulk. The B2B marketplace for circular fashion." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://hidnstock.com/" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero Section — clean, centered, full viewport */}
      <section className="relative min-h-[calc(100svh-8rem)] flex flex-col overflow-hidden border-b-4 border-foreground">
        <img src={heroImage} alt="Massive pile of secondhand textiles in a dark warehouse" loading="eager" className="absolute inset-0 w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/70 to-black/85" />

        <div className="container mx-auto px-6 lg:px-8 relative z-10 flex-1 flex flex-col items-center justify-between py-20 md:py-24 lg:py-28">
          {/* Top tagline */}
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-white/80 text-center">
            01 — {t("home.heroBadgeLine1")} / {t("home.heroBadgeLine2")}
          </p>

          {/* Big headline */}
          <div className="text-center max-w-5xl mx-auto px-2 sm:px-0 my-10 md:my-12">
            <h1
              className="font-black uppercase leading-[0.95] tracking-normal text-4xl sm:text-6xl md:text-7xl lg:text-[5.5rem] xl:text-[6.5rem] text-primary-foreground"
            >
              Anonymous to the market.
              <br />
              Known to HIDNstock.
            </h1>
            <p className="mt-7 text-base md:text-lg leading-relaxed text-primary-foreground/80 max-w-2xl mx-auto">Sell your excess while keeping your brand private. Buy with clear requirements for company checks and traceable proof on every lot.</p>
          </div>

          {/* Bottom CTA */}
          <div className="flex justify-center">
            <EarlyAccessDialog><Button size="lg" className="uppercase font-bold bg-white text-black border-2 border-white hover:bg-transparent hover:text-white">Get started</Button></EarlyAccessDialog>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 md:py-28 bg-secondary/40 bg-grain border-b-4 border-foreground">
        <div className="container mx-auto px-6 lg:px-8 max-w-4xl text-center">
          <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.3em] text-foreground/60 mb-5">
            {t("home.whatWeDo")}
          </p>
          <p className="text-lg md:text-xl text-foreground/85 leading-relaxed mb-12">
            {t("home.description")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <EarlyAccessDialog><Button size="lg" className="bg-foreground text-background border-2 border-foreground hover:bg-transparent hover:text-foreground">Get started</Button></EarlyAccessDialog>
            <Link to="/how-it-works">
              <Button size="lg" variant="outline" className="border-2 border-foreground text-foreground bg-transparent hover:bg-foreground hover:text-background w-full">
                {t("home.heroCtaLearn")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <TradeConfidence />

      {/* What We Offer */}
      <section className="relative py-24 bg-secondary/40 bg-grain overflow-hidden border-y-4 border-foreground">
        <span aria-hidden className="deco-text absolute -top-6 -left-4 text-[18vw] sm:text-[14vw] hidden sm:block">OFFER</span>
        <div className="container mx-auto px-6 lg:px-8 relative">
          <div className="text-center mb-12">
            <h2 className="mb-4 text-3xl md:text-4xl font-bold">{t("home.whatWeOffer")}</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{t("home.whatWeOfferDesc")}</p>
          </div>

          <Tabs defaultValue="browse" className="max-w-5xl mx-auto" onValueChange={handleTabChange}>
            <TabsList className="grid w-full grid-cols-2 h-auto p-1 mb-10">
              <TabsTrigger
                value="browse"
                onClick={handleTabChange}
                className={`py-3 px-2 sm:py-4 sm:px-6 text-xs sm:text-base transition-all duration-300 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2
                  data-[state=inactive]:scale-100 data-[state=inactive]:text-foreground/70 data-[state=inactive]:bg-transparent
                  data-[state=inactive]:hover:bg-primary data-[state=inactive]:hover:text-primary-foreground
                  data-[state=active]:scale-[1.02] data-[state=active]:shadow-lg data-[state=active]:text-primary-foreground
                  ${hasInteracted ? 'data-[state=active]:bg-primary data-[state=active]:hover:bg-primary' : 'data-[state=active]:bg-gray-600 data-[state=active]:hover:bg-primary'}
                `}
              >
                <Search className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="text-center leading-tight">
                  <span className="hidden sm:inline">{t("home.tabBrowseFull")}</span>
                  <span className="sm:hidden">{t("home.tabBrowseShort")}</span>
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="remake"
                className="py-3 px-2 sm:py-4 sm:px-6 text-xs sm:text-base transition-all duration-300 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2
                  data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground data-[state=active]:scale-[1.02] data-[state=active]:shadow-lg
                  data-[state=inactive]:scale-100 data-[state=inactive]:text-foreground/70 data-[state=inactive]:bg-transparent
                  data-[state=inactive]:hover:bg-secondary data-[state=inactive]:hover:text-secondary-foreground"
              >
                <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="text-center leading-tight">
                  <span className="hidden sm:inline">{t("home.tabRemakePrefix")}</span>{t("home.tabRemake")}
                </span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="browse" className="mt-0">
              <div ref={tabContentRef} className="text-center mb-10 scroll-mt-4">
                <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">{t("home.marketplaceBadge")}</Badge>
                <h3 className="text-2xl font-bold mb-3">{t("home.marketplaceTitle")}</h3>
                <p className="text-muted-foreground max-w-2xl mx-auto">{t("home.marketplaceDesc")}</p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="p-8">
                  <CardContent className="p-0">
                    <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mb-4">
                      <Package className="h-6 w-6 text-primary-foreground" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">{t("home.forSellers")}</h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed">{t("home.forSellersDesc")}</p>
                    <ul className="space-y-3">
                      <li className="flex items-center text-sm"><Shield className="h-4 w-4 mr-3 text-primary flex-shrink-0" />{t("home.sellerBenefit1")}</li>
                      <li className="flex items-center text-sm"><Recycle className="h-4 w-4 mr-3 text-primary flex-shrink-0" />{t("home.sellerBenefit2")}</li>
                      <li className="flex items-center text-sm"><Handshake className="h-4 w-4 mr-3 text-primary flex-shrink-0" />{t("home.sellerBenefit3")}</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="p-8">
                  <CardContent className="p-0">
                    <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mb-4">
                      <Users className="h-6 w-6 text-primary-foreground" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">{t("home.forBuyers")}</h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed">{t("home.forBuyersDesc")}</p>
                    <ul className="space-y-3">
                      <li className="flex items-center text-sm"><Package className="h-4 w-4 mr-3 text-primary flex-shrink-0" />{t("home.buyerBenefit1")}</li>
                      <li className="flex items-center text-sm"><CheckCircle className="h-4 w-4 mr-3 text-primary flex-shrink-0" />{t("home.buyerBenefit2")}</li>
                      <li className="flex items-center text-sm"><Handshake className="h-4 w-4 mr-3 text-primary flex-shrink-0" />{t("home.buyerBenefit3")}</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="remake" className="mt-0">
              <div className="text-center mb-10">
                <Badge className="mb-4 bg-secondary/20 text-secondary-foreground border-secondary/40 font-semibold px-4 py-1">{t("home.projectBadge")}</Badge>
                <h3 className="text-2xl font-bold mb-3">{t("home.projectTitle")}</h3>
                <p className="text-muted-foreground max-w-2xl mx-auto">{t("home.projectDesc")}</p>
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="p-8">
                  <CardContent className="p-0">
                    <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center mb-4">
                      <FileText className="h-6 w-6 text-secondary-foreground" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">{t("home.forBrandsPosting")}</h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed">{t("home.forBrandsPostingDesc")}</p>
                    <ul className="space-y-3">
                      <li className="flex items-center text-sm"><FileText className="h-4 w-4 mr-3 text-secondary flex-shrink-0" />{t("home.brandBenefit1")}</li>
                      <li className="flex items-center text-sm"><Users className="h-4 w-4 mr-3 text-secondary flex-shrink-0" />{t("home.brandBenefit2")}</li>
                      <li className="flex items-center text-sm"><Recycle className="h-4 w-4 mr-3 text-secondary flex-shrink-0" />{t("home.brandBenefit3")}</li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="p-8">
                  <CardContent className="p-0">
                    <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center mb-4">
                      <Recycle className="h-6 w-6 text-secondary-foreground" />
                    </div>
                    <h3 className="text-xl font-bold mb-4">{t("home.forRemakers")}</h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed">{t("home.forRemakersDesc")}</p>
                    <ul className="space-y-3">
                      <li className="flex items-center text-sm"><Search className="h-4 w-4 mr-3 text-secondary flex-shrink-0" />{t("home.remakerBenefit1")}</li>
                      <li className="flex items-center text-sm"><Handshake className="h-4 w-4 mr-3 text-secondary flex-shrink-0" />{t("home.remakerBenefit2")}</li>
                      <li className="flex items-center text-sm"><CheckCircle className="h-4 w-4 mr-3 text-secondary flex-shrink-0" />{t("home.remakerBenefit3")}</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* How It Works */}
      <section className="relative py-24 bg-gradient-to-b from-background via-secondary/30 to-background overflow-hidden">
        <span aria-hidden className="deco-text absolute top-8 right-[-2%] text-[16vw] sm:text-[12vw] hidden sm:block">PROCESS</span>
        <div className="container mx-auto px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <h2 className="mb-2 text-3xl md:text-4xl font-bold">{t("home.howItWorksTitle")}</h2>
            <p className="text-lg font-semibold text-primary mb-4">{t("home.howItWorksSubtitle")}</p>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">{t("home.howItWorksDesc")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-display font-semibold text-primary-foreground">1</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">{t("home.step1Title")}</h3>
              <p className="text-muted-foreground">{t("home.step1Desc")}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-secondary rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-display font-semibold text-secondary-foreground">2</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">{t("home.step2Title")}</h3>
              <p className="text-muted-foreground">{t("home.step2Desc")}</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-display font-semibold text-accent-foreground">3</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">{t("home.step3Title")}</h3>
              <p className="text-muted-foreground">{t("home.step3Desc")}</p>
            </div>
          </div>
          <div className="text-center mt-12">
            <Link to="/how-it-works" aria-label="Learn more about how HIDNstock works">
              <Button variant="outline" size="lg">
                {t("home.learnMore")}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="signup" className="py-24 bg-primary text-primary-foreground">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-xl mx-auto text-center">
            <h2 className="text-primary-foreground mb-6 text-3xl md:text-4xl font-bold">{t("home.ctaTitle")}</h2>
            <p className="text-xl text-primary-foreground/90 mb-8">{t("home.ctaDesc")}</p>
            <EarlyAccessDialog><Button size="lg" variant="secondary" className="w-full max-w-md">Get started</Button></EarlyAccessDialog>
            <p className="text-sm text-primary-foreground/70 mt-4">{t("home.ctaPrivacy")}</p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
